import React, { useCallback, useMemo, useRef, useState } from "react";
import classnames from "classnames";
import { Classes, Menu, MenuItem, Popover, TagInput } from "@blueprintjs/core";
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon";
import { DefaultSelectedOption, filterItemPredicate } from "modules/common/services/form/select";
import { texts } from "modules/common/texts";
import styles from "./PlanrMultiSelect.module.scss";

/**
 * Выбор нескольких значений. Образец — фильтры «Трекера задач»
 * (`order-questions/components/BoardFilters.tsx`, поле «Ответственный»).
 *
 * Поле выглядит как обычный `SimpleSelect`: пустое — подпись `what`, одно
 * значение — его название, несколько — количеством («Ответственных: 3»),
 * полный список — подсказкой при наведении. Крестик очищает выбор.
 *
 * По клику открывается список: сверху выбранное тегами и строка поиска
 * (курсор сразу в ней), ниже пункты с галочками. Клик по пункту отмечает
 * или снимает его, список остаётся открытым, поиск сбрасывается — следующее
 * значение ищут с чистого листа, не трогая мышь.
 *
 * Значение — массив `id`, как его хранят формы и фильтры.
 *
 * ```tsx
 * <PlanrMultiSelect what="Ответственный" many="Ответственных" items={people}
 *                   value={employerIds} onChange={setEmployerIds} placeholder="Поиск сотрудника" />
 * ```
 */
export function PlanrMultiSelect<T extends OptionItem>({
    items,
    value,
    onChange,
    what,
    many = "Выбрано",
    placeholder = "Поиск",
    renderTag,
    match = defaultMatch,
    disabled,
    highlight,
    popoverWidth = 320,
    className,
}: PlanrMultiSelectProps<T>) {
    const [query, setQuery] = useState("");
    const search = useRef<HTMLInputElement | null>(null);

    const chosen = useMemo(() => {
        const byId = new Map(items.map((item) => [item.id, item]));
        // id, которого нет в списке (уволенный сотрудник и т.п.), не показываем,
        // но и из значения не выкидываем — это решает форма, а не поле
        return value.map((id) => byId.get(id)).filter((item): item is T => !!item);
    }, [items, value]);

    const filtered = useMemo(() => (query ? items.filter((item) => match(query, item)) : items), [items, query, match]);

    const toggle = useCallback(
        (item: T) => {
            setQuery("");
            onChange(toggleId(value, item.id));
            // отмечать значения подряд удобно с клавиатуры: курсор возвращаем в поиск
            search.current?.focus();
        },
        [value, onChange]
    );

    // крестик гасит всплытие сам, список от очистки не открывается
    const clear = useCallback(() => onChange([]), [onChange]);

    const option =
        chosen.length === 0
            ? null
            : chosen.length === 1
            ? { id: chosen[0].id, label: chosen[0].label }
            : { id: "", label: `${many}: ${chosen.length}` };

    return (
        <div
            className={classnames(styles.root, { [styles.active]: highlight && chosen.length > 0 }, className)}
            title={chosen.length > 1 ? chosen.map((item) => item.label).join("\n") : undefined}
        >
            <Popover
                fill={true}
                minimal={true}
                disabled={disabled}
                rootBoundary="viewport"
                position="bottom-left"
                // те же классы, что навешивает SimpleSelect: поле выглядит как соседние селекты
                className={classnames("select", Classes.FILL, "planr-default-input")}
                onClosed={() => setQuery("")}
                content={
                    <div className={styles.list} style={{ width: popoverWidth }}>
                        <TagInput
                            className={styles.tags}
                            fill={true}
                            values={chosen.map((item) => (
                                <span key={item.id} title={item.label}>
                                    {renderTag ? renderTag(item) : item.label}
                                </span>
                            ))}
                            // индекс — позиция среди показанных тегов, а не в value:
                            // id без пункта в списке тегом не показан, и индексы бы разъехались
                            onRemove={(_value: React.ReactNode, index: number) => {
                                const item = chosen[index];
                                item && onChange(value.filter((id) => id !== item.id));
                            }}
                            inputValue={query}
                            onInputChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.currentTarget.value)}
                            inputProps={{ autoFocus: true, placeholder }}
                            inputRef={(el: HTMLInputElement | null) => {
                                search.current = el;
                            }}
                            rightElement={
                                chosen.length > 0 ? (
                                    <GeneralIcon
                                        type="general-cross-small"
                                        onClick={clear}
                                        title="Очистить"
                                        className={styles.clear}
                                    />
                                ) : undefined
                            }
                        />

                        {/* клик по пункту не должен уводить фокус из поиска */}
                        <Menu className={styles.menu} onMouseDown={(e: React.MouseEvent) => e.preventDefault()}>
                            {filtered.length === 0 && <MenuItem disabled={true} text={texts.noData} />}

                            {filtered.map((item) => (
                                <MenuItem
                                    key={item.id}
                                    icon={value.includes(item.id) ? "tick" : "blank"}
                                    text={item.label}
                                    // список остаётся открытым: значения отмечают подряд
                                    shouldDismissPopover={false}
                                    onClick={() => toggle(item)}
                                />
                            ))}
                        </Menu>
                    </div>
                }
            >
                <DefaultSelectedOption option={option as any} onClear={clear} what={what} disabled={disabled} />
            </Popover>
        </div>
    );
}

const defaultMatch = (query: string, item: OptionItem) => filterItemPredicate(query, item, 0, false) as boolean;

/** Выбрать `id`, если его нет в значении, или снять, если есть. Порядок выбранных сохраняется. */
export const toggleId = (value: string[], id: string) =>
    value.includes(id) ? value.filter((v) => v !== id) : [...value, id];

export interface PlanrMultiSelectProps<T extends OptionItem> {
    /** Все варианты. */
    items: T[];
    /** Выбранные `id`. */
    value: string[];
    onChange: (ids: string[]) => void;
    /** Подпись пустого поля: «Ответственный». */
    what: string;
    /** Родительный падеж множественного числа для счётчика: «Ответственных: 3». По умолчанию «Выбрано». */
    many?: string;
    /** Подсказка в строке поиска. */
    placeholder?: string;
    /** Короткая подпись тега (фамилия с инициалами, номер договора); по умолчанию `label`. */
    renderTag?: (item: T) => React.ReactNode;
    /** Своё сравнение с поиском; по умолчанию — вхождение в `label` без учёта регистра. */
    match?: (query: string, item: T) => boolean;
    disabled?: boolean;
    /** Выделить поле, когда что-то выбрано. Для фильтров: видно, что показаны не все записи. */
    highlight?: boolean;
    /** Ширина списка в px, по умолчанию 320 — хватает ФИО с должностью. */
    popoverWidth?: number;
    className?: string;
}
